export type StructureCode = 'cygnature' | 'suivez_vos_reves';

export type CanalContact = 'Téléphone' | 'Email' | 'WhatsApp' | 'SMS' | 'RDV physique' | 'Visio' | '';
export type Priorite = '1-🔥 Urgent' | '2-⚡ Important' | '3-✅ Confort' | '';

export const CANAUX_CONTACT: CanalContact[] = ['Téléphone', 'Email', 'WhatsApp', 'SMS', 'RDV physique', 'Visio'];
export const PRIORITES: Priorite[] = ['1-🔥 Urgent', '2-⚡ Important', '3-✅ Confort'];

export interface PrestationTache {
  id: string;
  prestationId: string;
  libelle: string;
  categorie?: string;
  responsable?: string;
  dateLimite?: string;
  fait: boolean;
  notes?: string;
  ordre: number;
}

export interface Prestation {
  id: string;
  structure: StructureCode;
  clientId?: string | null;
  client: string;
  tel: string;
  email: string;
  statut: string;
  dateEvent: string; // ISO yyyy-mm-dd
  lieu: string;
  km: number | null;
  duree: string;
  libelle: string;
  demande: string;
  services: string[];
  domaines: string[];
  themes?: string[];
  artistes: string[];
  propTTC: number | null;
  propNote: string;
  devis: number | null;
  acomptePct: number | null;
  contratClient: string;
  contratArtiste: string;
  remarques: string;
  factureEmise: string;
  paye: string;
  gcalId: string | null;
  dateCreation: string;
  updatedAt?: string;
  archivedAt: string | null;
  canal?: CanalContact;
  priorite?: Priorite;
  /** Somme des coûts artistes (depuis prestations_artistes.cout). */
  coutArtistes?: number | null;
  /** Détail tarifaire par artiste (depuis prestations_artistes). */
  artistesDetails?: { nom: string; cout: number | null; propTtc: number | null }[];
}

export interface FactureLigne {
  description: string;
  quantite: number;
  prixUnitaireHT: number;          // prix unitaire NET HT (après remise ligne)
  tvaPct: number;
  reference?: string | null;
  unite?: string | null;
  remisePct?: number;              // remise ligne en %
  prixBrutHT?: number | null;      // prix unitaire avant remise ligne
  typeLigne?: 'article' | 'section' | 'commentaire' | 'saut_page' | string;
}

export type FactureStatut = 'Brouillon' | 'Émise' | 'Envoyée' | 'Partiellement payée' | 'Payée' | 'En retard' | 'Annulée';

export interface Facture {
  id: string;                    // UUID interne
  numero: string;                // Ex: F-2026-0001 (séquentiel, immuable)
  structure: StructureCode;
  type: 'Facture' | 'Avoir' | 'Acompte' | 'Intermédiaire' | 'Solde';
  prestationId: string | null;   // Lien vers Prestation
  clientNom: string;
  clientAdresse: string;
  clientEmail: string;
  clientTel: string;
  clientSiret: string;           // Obligatoire B2B facturation électronique
  clientTvaIntra: string;        // N° TVA intracommunautaire
  dateEmission: string;          // ISO yyyy-mm-dd
  dateEcheance: string;          // ISO yyyy-mm-dd
  datePrestation: string;        // ISO
  lignes: FactureLigne[];
  remisePct: number;
  acompteVerse: number;
  modePaiement: string;
  conditionsPaiement: string;
  /** Échéancier détaillé (label, date, montant), repris de la proposition si disponible. */
  echeancier?: Array<{ label?: string; date?: string; montant?: number | null; paid?: boolean; paid_at?: string }>;
  mentionsLegales: string;
  notes: string;
  statut: FactureStatut;
  datePaiement: string;          // ISO si payée
  montantPaye: number;
  factorXGenere: boolean;        // Format Factur-X (PDF/A-3 + XML)
  chorusProTransmis: boolean;    // Pour clients publics
  pdpTransmis: boolean;          // Plateforme de Dématérialisation Partenaire
  clientPlateformeAgree?: string;   // PDP du client destinataire (pour routage e-invoicing 2026)
  dateCreation: string;
  devisId?: string | null;          // Devis d'origine (si conversion)
  prefixDevisOrigine?: string | null; // Numéro lisible du devis d'origine
  avoirOrigineId?: string | null;     // Facture d'origine (pour un avoir)
  finaliseeAt?: string | null;        // Date de finalisation (verrouillage)
}

export interface EmetteurInfo {
  raisonSociale: string;
  adresse: string;
  siret: string;
  tvaIntra: string;
  ape: string;
  rcs: string;
  capital: string;
  email: string;
  tel: string;
  iban: string;
  bic: string;
  logoUrl: string;
  tamponUrl?: string;
  prefixFacture: string;         // Ex: F-2026-
  prochainNumero: number;        // Compteur séquentiel
  prefixDevis?: string;
  prochainNumeroDevis?: number;
  plateformeAgree?: string;      // PDP de l'émetteur (Cygnature) — obligatoire 2026
}

// ============ CLIENTS ============
export type TypeClient = 'particulier' | 'entreprise' | 'collectivite' | 'association';
export interface Client {
  id: string;
  typeClient: TypeClient;
  nom: string;
  prenom?: string;
  societe?: string;
  fonction?: string;
  email?: string;
  tel?: string;
  telSecondaire?: string;
  adresse?: string;
  cp?: string;
  ville?: string;
  departement?: string;
  pays?: string;
  siret?: string;
  tvaIntra?: string;
  source?: string;
  segment?: string;
  tags?: string[];
  potentielBusiness?: string;
  notes?: string;
  createdAt?: string;
}

// ============ DEVIS ============
export type DevisStatut = 'Brouillon' | 'Envoyé' | 'Vu' | 'Relance' | 'Accepté' | 'Refusé' | 'Expiré' | 'Converti';

export interface DevisLigne {
  id?: string;
  ordre: number;
  titre?: string;
  description: string;
  quantite: number;
  prixUnitaireHT: number;
  tvaPct: number;
}

export interface Devis {
  id: string;
  numero: string;
  structure: StructureCode;
  statut: DevisStatut;
  prestationId: string | null;
  clientId: string | null;
  clientNom: string;
  clientAdresse?: string;
  clientEmail?: string;
  clientTel?: string;
  clientSiret?: string;
  clientTvaIntra?: string;
  objet?: string;
  lieu?: string;
  dateEvent?: string;
  noteIntro?: string;
  conditions?: string;
  dateEmission: string;
  dateValidite: string;
  acomptePct: number;
  modePaiement: string;
  nbEcheances?: number;
  remisePct: number;
  factureId?: string | null;
  convertiLe?: string | null;
  dateAcceptation?: string | null;
  lignes: DevisLigne[];
  createdAt?: string;
}

export const DEVIS_STATUT_COLORS: Record<DevisStatut, string> = {
  'Brouillon': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Envoyé': 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  'Vu': 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  'Relance': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Accepté': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Refusé': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Expiré': 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  'Converti': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

export interface RelanceHistoryEntry {
  id: string;
  date: string;        // ISO yyyy-mm-dd date où la relance a été marquée faite
  note?: string;       // annotation libre
  auteur?: string;     // email/nom utilisateur
}

/** Produit du catalogue : décoration, location, prestation de services… */
export interface Produit {
  id: string;
  categorie: string;
  libelle: string;
  description: string;
  coutHT: number;
  prixHT: number;
  unite: string;
  actif: boolean;
  ordre: number;
  reference?: string;
  sousFamille?: string;
  tvaPct?: number;
  fournisseur?: string;
}

/** Produit rattaché à un dossier (calculateur). */
export interface PrestationProduit {
  id: string;
  prestationId: string;
  produitId: string | null;
  libelle: string;
  description: string;
  quantite: number;
  coutHT: number;
  prixHT: number;
  ordre: number;
}

export interface CrmData {
  version: number;
  exportDate: string;
  prest: Prestation[];
  options: string[];
  services: string[];
  domaines: string[];
  themes: string[];
  artistes: string[];
  paiementModes: string[];
  produits?: Produit[];
  produitCategories?: string[];
  relanceNotes: Record<string, string>;
  relanceHistory?: Record<string, RelanceHistoryEntry[]>;
  factures: Facture[];
  emetteur: EmetteurInfo;                            // Cygnature (legacy / défaut)
  emetteurs?: Record<StructureCode, EmetteurInfo>;   // Map par structure
  clients?: Client[];
  devis?: Devis[];
}



export const FACTURE_STATUT_COLORS: Record<FactureStatut, string> = {
  'Brouillon': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Émise': 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  'Envoyée': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  'Partiellement payée': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Payée': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'En retard': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Annulée': 'bg-slate-700/40 text-slate-400 border-slate-600/40',
};

export const STATUTS = [
  'Brouillon',
  'Demande de Rdv',
  'Propal A FAIRE',
  'Propal transmise',
  'Relance',
  'Relance Tel, info transmise',
  'Contrat Transmis',
  'Signé',
  'Refusée',
  'Projet en pause',
  'Payé & Livré',
] as const;

export const STATUT_COLORS: Record<string, string> = {
  'Signé': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Refusée': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Relance': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Relance Tel, info transmise': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Propal transmise': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  'Propal A FAIRE': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  'Demande de Rdv': 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  'Contrat Transmis': 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  'Projet en pause': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Brouillon': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Brouillon abandonné': 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  'Expirée': 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  'Réalisée': 'bg-emerald-700/30 text-emerald-300 border-emerald-700/40',
  'Payé & Livré': 'bg-emerald-600/25 text-emerald-200 border-emerald-500/50',
};
